import { createContext, useContext, useEffect } from 'react';

/**
 * The context behind the fullscreen forms' section navigation — see
 * formSectionNav.tsx for the provider, the rail and the app-bar menu.
 */

export type FormSection = {
    id: string;
    /** i18n id of the section heading, reused verbatim from the section box. */
    titleId: string;
};

export type FormSectionNavValue = {
    sections: FormSection[];
    activeId: string | null;
    setActiveId: (id: string) => void;
    /**
     * Hand the scroll container to the provider. A setter rather than a shared
     * ref: the provider owns that ref, and a consumer writing into a value it
     * got out of a hook is what react-hooks/immutability warns about.
     */
    setContainer: (node: HTMLDivElement | null) => void;
    scrollTo: (id: string) => void;
};

export const FormSectionNavContext = createContext<FormSectionNavValue | null>(
    null
);

/** The rail/menu only earn their space once there is more than one section. */
export function hasNav(
    value: FormSectionNavValue | null
): value is FormSectionNavValue {
    return !!value && value.sections.length >= 2;
}

export function useFormSectionNav() {
    return useContext(FormSectionNavContext);
}

/**
 * Watch the scroll container and mark the section nearest the top as active.
 * Called by {@link FormSectionsBody}, which owns the container ref. The bottom
 * `rootMargin` shrinks the "hit zone" to the top third so a section lights up
 * as its heading reaches the top.
 */
export function useSectionScrollSpy(
    nav: FormSectionNavValue | null,
    container: HTMLDivElement | null
) {
    const sectionsKey = nav?.sections.map((entry) => entry.id).join('|');
    const setActiveId = nav?.setActiveId;

    useEffect(() => {
        if (!hasNav(nav) || !container || !setActiveId) {
            return;
        }
        const visible = new Set<string>();
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = (entry.target as HTMLElement).dataset
                        .sectionAnchor;
                    if (!id) {
                        continue;
                    }
                    if (entry.isIntersecting) {
                        visible.add(id);
                    } else {
                        visible.delete(id);
                    }
                }
                // Active = the first section (in document order) still in the
                // hit zone; between sections we keep the last one.
                const firstVisible = nav.sections.find((entry) =>
                    visible.has(entry.id)
                );
                if (firstVisible) {
                    setActiveId(firstVisible.id);
                }
            },
            { root: container, rootMargin: '0px 0px -66% 0px', threshold: 0 }
        );
        const anchors = container.querySelectorAll('[data-section-anchor]');
        anchors.forEach((anchor) => observer.observe(anchor));
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [container, sectionsKey, setActiveId]);
}
